import { TFunction } from "i18next";
import { SliceState } from "../../localized/localizedApi";
import { adaptLegacyHrTask } from "./adaptLegacyHrTask";
import {
  HrTask,
  HrTaskResponse,
  HrTaskRowText,
  HrTaskContentRow,
  HrTaskContentRowType,
  HrTaskActionType,
  HrTaskType,
} from "./types";

const sickLeaveHrTaskTypes = new Set<HrTaskType>([
  HrTaskType.SickLeaveShortAbsencesX3,
  HrTaskType.SickLeaveLongAbsence,
  HrTaskType.SickLeaveEntryThreshold,
]);

export interface HrTaskAdapterContext {
  language: string;
  t: TFunction;
  localized: SliceState;
}

export const toHrTask = (
  response: HrTaskResponse,
  context: HrTaskAdapterContext,
): HrTask => {
  // Partial documents must retain the entire legacy contract.
  if (response.presentation == null || response.actions == null) {
    return adaptLegacyHrTask(response, context);
  }

  const { language, t } = context;
  const rows = (content: HrTaskContentRow[]): HrTaskContentRow<string>[] =>
    content.map((row) => ({ ...row, text: localizedText(row.text, language) }));
  const title = localizedText(response.presentation.title, language);
  const confirmation = response.actions.confirmation;
  // YLPE-7161: Keep cards summary-only until stored backend presentations omit absence details.
  // The first row is the headline; the full details remain in confirmation.rows.
  const cardRows = sickLeaveHrTaskTypes.has(response.type)
    ? response.presentation.rows.slice(0, 1)
    : response.presentation.rows;

  return {
    id: response.id,
    canManuallyAcknowledge: response.canManuallyAcknowledge,
    presentation: {
      title,
      rows: rows(cardRows),
      ...(response.employerName ? { density: "compact" as const } : {}),
    },
    actions: {
      defaultAction: response.actions.defaultAction,
      confirmation: confirmation
        ? {
            ...confirmation,
            title: localizedText(confirmation.title, language),
            rows: rows(confirmation.rows),
          }
        : {
            type: HrTaskActionType.CONFIRM_COMPLETION,
            title,
            rows: [
              {
                type: HrTaskContentRowType.TEXT,
                text: t("hrTasklist.markAsDoneConfirmation"),
              },
            ],
          },
    },
  };
};

const localizedText = (text: HrTaskRowText, language: string): string => {
  const baseLanguage = language.toLowerCase().split(/[-_]/)[0];
  const translated =
    baseLanguage === "fi" || baseLanguage === "en" || baseLanguage === "sv"
      ? text[baseLanguage]
      : undefined;
  return translated?.trim() ? translated : text.fi;
};
